import { Room } from "shared/types/Room"

/**
 * Reconciles a room/child selection cascade after a single toggle.
 *
 * Maintains the invariant: a room's ID is in the selection if and only if ALL
 * of that room's children are in the selection.
 *
 * Rules:
 * 1. Room toggled ON  → add room + all its children
 * 2. Room toggled OFF → remove room + all its children
 * 3. Child toggled OFF → remove child + remove parent room (no longer full)
 * 4. Child toggled ON  → add child; if all siblings now selected, also add room
 *
 * If the diff between prevSelected and nextSelected is not exactly one ID,
 * returns nextSelected unchanged (defensive).
 *
 * Products that have no parent room (unassigned products) just pass through.
 */
export function reconcileRoomSelection(
  prevSelected: string[],
  nextSelected: string[],
  rooms: Room[]
): string[] {
  const prevSet = new Set(prevSelected)
  const nextSet = new Set(nextSelected)

  const added = nextSelected.filter(id => !prevSet.has(id))
  const removed = prevSelected.filter(id => !nextSet.has(id))

  // Defensive: only handle single-ID toggles
  if (added.length + removed.length !== 1) {
    return nextSelected
  }

  // Build lookup maps from rooms
  const roomChildIDs = new Map<string, string[]>()
  const childToRoomID = new Map<string, string>()

  rooms.forEach(room => {
    const children = room.children ?? []
    const childIDs = children.map(c => c.id)
    roomChildIDs.set(room.id, childIDs)
    childIDs.forEach(childID => childToRoomID.set(childID, room.id))
  })

  const result = new Set(nextSet)

  if (added.length === 1) {
    const toggledID = added[0]

    if (roomChildIDs.has(toggledID)) {
      // Rule 1: room toggled ON → add room + all children
      ;(roomChildIDs.get(toggledID) ?? []).forEach(childID =>
        result.add(childID)
      )
    } else {
      // Child toggled ON
      const parentRoomID = childToRoomID.get(toggledID)
      if (parentRoomID !== undefined) {
        // Rule 4: if all siblings now selected, add parent room
        const allSiblingIDs = roomChildIDs.get(parentRoomID) ?? []
        const allSelected = allSiblingIDs.every(id => result.has(id))
        if (allSelected && allSiblingIDs.length > 0) {
          result.add(parentRoomID)
        }
      }
      // else: no parent room (unassigned product) — nextSelected is already correct
    }
  } else {
    const toggledID = removed[0]

    if (roomChildIDs.has(toggledID)) {
      // Rule 2: room toggled OFF → remove room + all children
      ;(roomChildIDs.get(toggledID) ?? []).forEach(childID =>
        result.delete(childID)
      )
    } else {
      // Child toggled OFF
      const parentRoomID = childToRoomID.get(toggledID)
      if (parentRoomID !== undefined) {
        // Rule 3: remove parent room (no longer fully selected)
        result.delete(parentRoomID)
      }
      // else: no parent room (unassigned product) — nextSelected is already correct
    }
  }

  return Array.from(result)
}
